import { DateTime, Duration as DateTimeDuration, Interval as DateTimeInterval } from 'luxon'

import { runCode, RunOptions } from '../../dynamic/dynamic-code.js'
import { isDocument } from '../../model/is.js'
import type { CommandAeppicInterface } from './executor.js'

export interface ConditionResult {
  ok: boolean
  error?: string
  value?: any
  reason?: string
}

export interface ConditionEvaluation {
  id: string
  name: string
  passed: boolean
  reason?: string
  error?: string
}

export interface ConditionOptions {
  noIsolation?: boolean
}

export type ScriptFunction = {
  ok: true
  fn: any
} | 
{
  ok: false
} 

const DEFAULT_CONDITION_OPTIONS: RunOptions = {
  isolation: 'best',
  functionExecution: 'async'
}

export class ConditionExecutor {
  private _aeppic: CommandAeppicInterface

  constructor({ Aeppic }: { Aeppic: CommandAeppicInterface }) {
    this._aeppic = Aeppic
    Object.freeze(this)
  }

  async allPassing(conditions, target, conditionParameters, options: ConditionOptions = {}) {
    const results = await this.evaluate(conditions, target, conditionParameters, options)
    return results.every(result => result.passed)
  }

  async failingConditions(conditions, target, conditionParameters, options: ConditionOptions = {}) {
    const results = await this.evaluate(conditions, target, conditionParameters, options)
    return results.filter(result => !result.passed)
  }

  async evaluate(conditions, target, conditionParameters, options: ConditionOptions = {}): Promise<ConditionEvaluation[]> {
    if (!conditions.length) {
      return []
    }

    const targetDocument = isDocument(target) ? target : await this._aeppic.get(target)
    if (!targetDocument) {
      throw new Error(`Unknown target or target not accessible (${JSON.stringify(target)})`)
    }

    const context = { target: targetDocument, params: conditionParameters || {} }
    const runOptions = { isolation: options.noIsolation ? 'none' : 'best' }

    return Promise.all(conditions.map(async condition => {
      const result = await this._runCondition(condition, context, runOptions)
      return {
        id: condition.id,
        name: condition.data?.name || condition.data?.label || condition.id,
        passed: result.ok && !!result.value,
        reason: result.reason,
        error: result.ok ? undefined : 'Condition evaluation failed',
      }
    }))
  }

  private async _runCondition(condition, context, specifiedOptions): Promise<ConditionResult> {
    const options = { ...DEFAULT_CONDITION_OPTIONS, ...specifiedOptions }

    let reason: string | undefined
    const setReason = (text: string) => {
      if (typeof text !== 'string') {
        throw new TypeError('context.setReason expects a string')
      }
      reason = text.trim() || undefined
    }

    const globals = {
      context: { ...context, setReason },
      params: context.params,
      Aeppic: this._aeppic,
      DateTime,
      DateTimeDuration,
      DateTimeInterval,
      Math,
      setTimeout: RaiseUnsupportedFunction,
      setInterval: RaiseUnsupportedFunction,
    }

    const result = await runCode(condition, globals, options)
    return { ...result, reason }
  }
}

function RaiseUnsupportedFunction() {
  throw new Error('Unsupported function error')
}
